import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { XpoShellRoute } from '@xpo-ltl/ngx-ltl-core/shell';
import { User } from '@xpo-ltl/sdk-common';
import { invoke as _invoke, isEmpty as _isEmpty } from 'lodash';
import { interval } from 'rxjs';
import { skipWhile, take, tap } from 'rxjs/operators';
import { NotificationMenuItem } from '@models/notification-menu-item.model';
import { ObiApiService } from '@services/incentive/obi-api.service';
import { AppRoutes } from '@enums/app-routes.enum';
import { ConfigManagerProperties } from './enums/config-manager-properties.enum';
import { AppMainMenu } from './enums/app-main-menu.enum';
import { Util } from '@enums/util.enum';
import { Title } from '@angular/platform-browser';
import { UserRoleService } from '@services/user-role/user-role.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { UserRamRoleSelectionComponent } from './shared/dialog/user-ram-role-selection/user-ram-role-selection.component';
import { Router } from '@angular/router';

interface XpoShellRouteSecure extends XpoShellRoute {
  permission?: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
  appVersion: string;
  allowedRoutes: XpoShellRoute[];
  menuItems = AppMainMenu;
  notifications: NotificationMenuItem[];
  notificationInterval;
  roleList: string;
  region: string = '';
  isProduction: boolean = false;
  ramRoles: string[] = [];
  readonly releaseNotesURL: string =
    'https://xpologistics.sharepoint.com/:f:/r/sites/150/projects/LTLOBI/Monitor%20and%20Control/Release%20Notes?csf=1&web=1&e=oy1LyM';
  readonly customerBaseURL: string = 'https://cbase.ltl-xpo.com/customer-search';
  readonly shipmentManagementURL: string = 'https://shplist.ltl-xpo.com/home';
  readonly ratingURL: string = 'https://rating.ltl-xpo.com/rating-analysis';
  readonly lookerReportURL: string = 'https://xpologisticsprod.cloud.looker.com/looks/165';

  allRoutes: XpoShellRouteSecure[] = [
    {
      label: AppMainMenu.offBillIncentiveList,
      path: `/${AppRoutes.OFF_BILL_INCENTIVE}/${AppRoutes.LIST_PAGE}`,
    },
    {
      label: AppMainMenu.agreementList,
      path: `/${AppRoutes.OBI_AGREEMENT}/${AppRoutes.LIST_PAGE}`,
    },
  ];

  constructor(
    private configManagerService: ConfigManagerService,
    private userRoleService: UserRoleService,
    private obiService: ObiApiService,
    private titleService: Title,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.isProduction = this.configManagerService.getSetting<boolean>(ConfigManagerProperties.production);
    this.region = this.configManagerService.getSetting<string>(ConfigManagerProperties.region);
    this.appVersion = configManagerService.getSetting<string>(ConfigManagerProperties.buildVersion);
    if (!this.isProduction) this.titleService.setTitle(`OBI - ${this.region}`);
  }

  ngOnInit(): void {
    this.obiService.offBillIncentiveApiService
      .loggedInUser()
      .pipe(take(1))
      .subscribe(
        (user: User) => {
          if (user) {
            this.userRoleService.user = user;
            this.ramRoles = this.userRoleService.getRamRoles(user.roles);
            this.userRoleService.ramRoles.next(this.ramRoles);
            this.userRoleService.userLoaded$.next(true);
            this.setDynatraceUserIdentity(user);
            this.getAllowedRoutes(false);
          }
        },
        (error) => {
          console.error('Error loading user after auth initialization:', error);
        }
      );
  }

  openRamRolesSwitchDialog() {
    const dialogRef = this.dialog.open(UserRamRoleSelectionComponent, {
      width: '500px',
      height: '500px',
      data: {
        ramRoles: this.ramRoles,
      },
    });
    dialogRef.afterClosed().subscribe((resp: string[]) => {
      if (Array.isArray(resp)) {
        this.ramRoles = resp;
        this.userRoleService.ramRoles.next(resp);
        this.getAllowedRoutes(true);
      }
    });
  }

  getAllowedRoutes(updatedRamRoleSelection: boolean = false) {
    if (this.userRoleService.isAuthorizedUser()) {
      this.allowedRoutes = this.allRoutes;
      this.obiService.getNotifications();

      if (this.notificationInterval) {
        clearInterval(this.notificationInterval);
      }
      this.notificationInterval = setInterval(this.obiService.getNotifications, Util.timeout);
      if (updatedRamRoleSelection) this.userRoleService.routeBasedOnPermissions();
    } else {
      this.allowedRoutes = [];
      this.router.navigate([AppRoutes.NOT_AUTHORIZED_PAGE]);
    }
  }

  populateAccountPopover(): boolean {
    return this.userRoleService.isAllowedToEditIncentive;
  }

  isMenuAllowed(menu: string): boolean {
    return this.allowedRoutes?.some((route) => route.label === menu) ?? false;
  }

  private setDynatraceUserIdentity(user: User): void {
    const setUser = (): void =>
      _invoke(window['dtrum'], 'identifyUser', !_isEmpty(user.emailAddress) ? user.emailAddress : user.userId);
    if ((window['dtrum'] || {})['identifyUser']) {
      setUser();
    } else {
      let retryCount: number = 0;
      interval(1000)
        .pipe(
          tap(() => retryCount++),
          skipWhile(() => !(window['dtrum'] || {})['identifyUser'] && retryCount <= 60),
          take(1)
        )
        .subscribe(() => {
          setUser();
        });
    }
  }
}
